import agentAxios from '@/lib/api'

export interface AgentStats {
  wallet_balance: number
  free_trials_left: number
  current_month_sales: number
  current_month_orders: number
  agent_code: string
  credit_balance: number
  bonus_balance: number
  referred_by?: string | null
  referred_by_email_masked?: string | null
  display_name?: string
  phone?: string | null
  address?: string | null
  zalo_link?: string | null
  facebook_link?: string | null
  tiktok_link?: string | null
  secure_info_status: 'not_submitted' | 'pending' | 'approved' | 'rejected'
  tax_info: {
    legal_name: string
    bank_name: string
    bank_account_number: string
    bank_account_holder: string
    id_card_number?: string
    tax_code?: string
  } | null
}

export interface TrialCode {
  code: string
  created_by_user_id: number
  credit_amount: number
  status: 'unused' | 'activated'
  activated_by?: string | null
  activated_at?: number | null
  created_at: number
}

export interface ReferredPayment {
  id?: number
  user_id: number
  username: string
  referrer_code: string | null
  package_id: string
  price_paid_vnd: number
  credit_amount_base: number
  credit_amount_promo: number
  credit_amount_bonus_buyer: number
  credit_amount_bonus_referrer: number
  transaction_ref: string
  order_code?: number
  payment_date: number
}

export interface PayoutRequest {
  id: number
  user_id: number
  username: string
  credit_amount: number
  amount_vnd: number
  bank_name: string
  bank_account_number: string
  bank_account_holder: string
  legal_name: string
  id_card_number: string
  tax_code: string
  status: 'pending' | 'completed' | 'rejected'
  created_at: number
  updated_at: number
}

export const agentAPI = {
  /** GET /v1/user/wallet */
  getStats: async (): Promise<AgentStats> => {
    const response = await agentAxios.get('/v1/user/wallet')
    const data = response.data?.data
    const creditBalance = data?.credit_balance ?? 0
    const bonusBalance = data?.bonus_balance ?? 0
    return {
      wallet_balance: creditBalance * 100,
      free_trials_left: 0,
      current_month_sales: 0,
      current_month_orders: 0,
      agent_code: data?.referral_code || '',
      credit_balance: creditBalance,
      bonus_balance: bonusBalance,
      referred_by: data?.referred_by || null,
      referred_by_email_masked: data?.referred_by_email_masked || null,
      secure_info_status: 'not_submitted',
      tax_info: null
    }
  },

  /** GET /v1/user/wallet/gift-codes */
  listTrialCodes: async (page = 1, size = 20, status?: string): Promise<{ codes: TrialCode[]; total: number }> => {
    const response = await agentAxios.get('/v1/user/wallet/gift-codes', { params: { page, size, status: status || undefined } })
    return {
      codes: response.data?.data?.codes ?? [],
      total: response.data?.pagination?.total ?? 0
    }
  },

  /** POST /v1/user/wallet/gift-codes/generate */
  buyTrialCodes: async (quantity: number) => {
    const response = await agentAxios.post('/v1/user/wallet/gift-codes/generate', { quantity })
    return response.data
  },

  /** POST /v1/user/activate-gift-code */
  activateGiftCode: async (code: string): Promise<any> => {
    const response = await agentAxios.post('/v1/user/activate-gift-code', { code })
    return response.data
  },

  /** POST /v1/user/wallet/withdraw */
  withdraw: async (data: {
    credit_amount: number
  }) => {
    const response = await agentAxios.post('/v1/user/wallet/withdraw', data)
    return response.data
  },

  /** GET /v1/user/wallet/withdraw/history */
  listPayoutHistory: async (page = 1, size = 20): Promise<{ payouts: PayoutRequest[]; pagination: { page: number; size: number; total: number } }> => {
    const response = await agentAxios.get('/v1/user/wallet/withdraw/history', { params: { page, size } })
    return {
      payouts: response.data?.data?.payouts ?? [],
      pagination: response.data?.pagination ?? { page, size, total: 0 }
    }
  },

  /** GET /v1/user/wallet/referred-payments */
  listReferredPayments: async (page = 1, size = 20): Promise<{ payments: ReferredPayment[]; pagination: { page: number; size: number; total: number } }> => {
    const response = await agentAxios.get('/v1/user/wallet/referred-payments', { params: { page, size } })
    return {
      payments: response.data?.data?.payments ?? [],
      pagination: response.data?.pagination ?? { page, size, total: 0 }
    }
  },

  /** POST /v1/user/wallet/tax-profile */
  submitSecureInfo: async (data: {
    legal_name: string
    id_card_number: string
    tax_code: string
    bank_name: string
    bank_account_number: string
    bank_account_holder: string
    legal_type: 'individual' | 'business'
    id_card_front_url: string
    id_card_back_url: string
    business_license_url?: string | null
  }) => {
    const response = await agentAxios.post('/v1/user/wallet/tax-profile', data)
    return response.data
  },

  /** POST /v1/user/wallet/tax-profile/upload */
  uploadTaxProfileDoc: async (file: File, docType: string): Promise<{ url: string; key: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('doc_type', docType)
    const response = await agentAxios.post('/v1/user/wallet/tax-profile/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data?.data
  },
  
  /** POST /v1/user/wallet/tax-profile/delete-document */
  deleteTaxProfileDoc: async (docType: string): Promise<any> => {
    const response = await agentAxios.post('/v1/user/wallet/tax-profile/delete-document', { doc_type: docType })
    return response.data
  },

  /** GET /v1/user/wallet/tax-profile */
  getTaxProfile: async () => {
    const response = await agentAxios.get('/v1/user/wallet/tax-profile')
    return response.data?.data ?? null
  },

  /** GET /v1/user/wallet/monthly-bonus */
  getMonthlyBonusStatus: async (): Promise<MonthlyBonusStatus> => {
    const response = await agentAxios.get('/v1/user/wallet/monthly-bonus')
    return response.data?.data
  },

  /** POST /v1/user/wallet/monthly-bonus/claim */
  claimMonthlyBonus: async (milestoneId?: number) => {
    const response = await agentAxios.post('/v1/user/wallet/monthly-bonus/claim', { milestone_id: milestoneId })
    return response.data
  },

  /** GET /v1/user/wallet/banks */
  getSupportedBanks: async (): Promise<Array<{ code: string; short_name: string; name: string; bin: string | null; logo: string | null }>> => {
    const response = await agentAxios.get('/v1/user/wallet/banks')
    return response.data?.data?.banks ?? []
  },

  /** GET /v1/credit-packages */
  getCreditPackages: async (): Promise<{ packages: CreditPackage[]; zalo_group_url?: string; facebook_group_url?: string }> => {
    const response = await agentAxios.get('/v1/credit-packages')
    return {
      packages: response.data?.data?.packages ?? [],
      zalo_group_url: response.data?.data?.zalo_group_url,
      facebook_group_url: response.data?.data?.facebook_group_url
    }
  },

  /** POST /v1/user/payment/create */
  createPaymentOrder: async (data: { package_id: string; referrer_code?: string }): Promise<{ checkout_url: string; order_code: number; gateway: 'mock' | 'payos' | 'bank_transfer' }> => {
    const response = await agentAxios.post('/v1/user/payment/create', data)
    return response.data?.data ?? null
  },

  /** POST /v1/user/payment/simulate-success */
  simulatePaymentSuccess: async (orderCode: number) => {
    const response = await agentAxios.post('/v1/user/payment/simulate-success', { order_code: orderCode })
    return response.data
  },

  /** GET /v1/user/payment/pending */
  getPendingPaymentOrder: async (): Promise<PaymentOrder | null> => {
    const response = await agentAxios.get('/v1/user/payment/pending')
    return response.data?.data?.pending ?? null
  },

  /** POST /v1/user/payment/cancel */
  cancelPendingPaymentOrder: async (): Promise<void> => {
    await agentAxios.post('/v1/user/payment/cancel')
  },

  /** GET /v1/user/payment/validate-referrer */
  validateReferrerCode: async (code: string): Promise<{ valid: boolean; referral_code: string; email_masked: string | null }> => {
    const response = await agentAxios.get(`/v1/user/payment/validate-referrer?code=${encodeURIComponent(code)}`)
    return response.data?.data ?? null
  },

  /** GET /v1/user/payment/check-status */
  checkPaymentStatus: async (orderCode: number): Promise<{ status: 'completed' | 'cancelled' | 'pending' }> => {
    const response = await agentAxios.get(`/v1/user/payment/check-status?order_code=${orderCode}`)
    return response.data?.data ?? null
  },

  /** GET /v1/user/wallet/transactions */
  getCreditTransactions: async (page = 1, size = 10, isBonus?: number): Promise<{ transactions: any[]; pagination: { page: number; size: number; total: number } }> => {
    const response = await agentAxios.get(`/v1/user/wallet/transactions`, { 
      params: { 
        page, 
        size, 
        is_bonus: isBonus !== undefined ? isBonus : undefined 
      } 
    })
    return {
      transactions: response.data?.data?.transactions ?? [],
      pagination: {
        page: response.data?.pagination?.page ?? page,
        size: response.data?.pagination?.size ?? size,
        total: response.data?.pagination?.total ?? 0
      }
    }
  },

  // Stub unused legacy actions
  register: async (data: any) => { return { success: true } },
  updateProfile: async (data: any) => { return { success: true } },
  convertCommissionToCredits: async (amountVnd: number) => { return { success: true } }
}

export interface MilestoneConfig {
  id: number
  sales_milestone_vnd: number
  bonus_credit_amount: number
  description: string
  claimed: boolean
  claimable: boolean
}

export interface MonthlyBonusStatus {
  current_month: string
  current_sales_amount: number
  prev_month: string
  prev_sales_amount: number
  prev_has_claimed: boolean
  prev_claimed_milestones: number[]
  prev_highest_milestone: MilestoneConfig | null
  milestones: MilestoneConfig[]
}

export interface CreditPackage {
  id: string
  name: string
  price_vnd: number
  credit_amount: number
  promo_bonus_percent: number
  is_active: number
}

export interface PaymentOrder {
  order_code: number
  user_id: number
  username: string
  referrer_code?: string | null
  package_id: string
  price_paid_vnd: number
  credit_amount_base: number
  credit_amount_promo: number
  credit_amount_bonus_buyer: number
  credit_amount_bonus_referrer: number
  status: 'pending' | 'completed' | 'cancelled'
  payment_link_id?: string | null
  checkout_url: string
  created_at?: number
}
